// --- React
import React, { useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
// --- External UI Libraries
import ExpandableSection from "@dtci/spellbook/ExpandableSection";
import { MenuItem } from "@dtci/spellbook/Menu";
// --- Utils
import { persistBeforeNavTo } from "./navStorage.js";

/**
 * Sidebar nav row matching Spellbook ExpandableMenuSection layout, with a real
 * anchor for native open-in-new-tab and copy-link behavior.
 *
 * @param {Object} props
 * @param {string} props.path - Destination pathname (e.g. "/home")
 * @param {string} props.to - Full path+search from {@link buildNavTo}
 * @param {boolean} [props.isActive] - Whether this route is selected
 * @param {React.ReactNode} props.mainSection - Icon or leading content
 * @param {React.ReactNode} [props.children] - Label in the expanded section
 * @param {string} [props.className] - Extra class on the menu li
 * @param {Object} [props.containerProps] - Passed to ExpandableSection Container
 * @param {Object} [props.testMetadata] - Spellbook test metadata
 */
export function TapNavMenuSection({
  path,
  to,
  isActive,
  mainSection,
  children,
  className,
  containerProps,
  testMetadata,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * Matches ExpandableMenu with isExpanded and default context when there is
   * no provider (always expanded in TAP).
   */
  const isExpanded = true;

  const menuItemClasses = useMemo(
    () =>
      [
        "sb-expandable-menu-section",
        isActive && "sb-expandable-menu-section__active",
        isExpanded && "sb-expandable-menu-section__expanded",
        !isExpanded && "sb-expandable-menu-section__collapsed",
        className,
      ]
        .filter(Boolean)
        .join(" "),
    [isActive, isExpanded, className],
  );

  const handleNavClick = (e) => {
    persistBeforeNavTo(location.pathname, location.search, path);
    const modified =
      e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
    if (modified) {
      return;
    }
    e.preventDefault();
    navigate(to);
  };

  return (
    <MenuItem className={menuItemClasses}>
      <NavLink
        to={to}
        className="TapNavMenuSection-link"
        end
        onClick={handleNavClick}
      >
        <ExpandableSection
          isExpanded={isExpanded}
          mainSection={mainSection}
          containerProps={containerProps}
          testMetadata={testMetadata}
          className="sb-expandable-menu-section__expandable-section"
        >
          {children}
        </ExpandableSection>
      </NavLink>
    </MenuItem>
  );
}
